import { forwardRef, useCallback } from "react";
import PropTypes from "prop-types";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { $getRoot } from "lexical";

import { cn } from "@/lib/utils";
import { isSafeUrl } from "components/helpers";

import editorTheme from "./editorTheme";
import { EDITOR_NODES } from "./nodes";
import EditablePlugin from "./plugins/EditablePlugin";
import EditorApiPlugin from "./plugins/EditorApiPlugin";
import LinkHoverPlugin from "./plugins/LinkHoverPlugin";
import MentionsPlugin from "./plugins/MentionsPlugin";
import ToolbarPlugin from "./plugins/ToolbarPlugin";

// Lexical handles its shortcuts but lets the event bubble, so without this a
// global window binding such as the sidebar's Ctrl+B would also fire.
function stopShortcutPropagation(event) {
  if (event.ctrlKey || event.metaKey) {
    event.stopPropagation();
  }
}

const RichTextEditor = forwardRef(function RichTextEditor(props, ref) {
  const {
    editorState,
    content,
    onStateChange = () => {},
    readOnly = false,
    style,
    className,
    contentClassName,
    placeholder,
    ariaLabel,
    autoFocus,
    onAttach,
    templates,
    onSelectTemplate,
    categories,
    categoryValue,
    onChangeCategory,
    onManageCategories,
    isFullscreen,
    onToggleFullscreen,
  } = props;

  // Read once by LexicalComposer; every prop that can change afterwards is
  // handled by a plugin effect instead.
  const initialConfig = {
    namespace: "rvx-rich-editor",
    nodes: EDITOR_NODES,
    theme: editorTheme,
    editable: !readOnly,
    onError: error => {
      throw error;
    },
  };

  const handleChange = useCallback(
    state => {
      const hasText = state.read(
        () => $getRoot().getTextContent().trim().length > 0
      );
      onStateChange({ editorState: state, hasText });
    },
    [onStateChange]
  );

  // A read-only editor is a rendered document, not a field: it carries no
  // chrome of its own so it can sit inside a note card or an audit entry.
  const shell = readOnly
    ? "tw:text-[var(--color-text-primary)]"
    : "tw:rounded-lg tw:border tw:border-[var(--color-border)] tw:bg-[var(--color-surface)] tw:text-[var(--color-text-primary)]";

  return (
    <div className={cn(shell, className)} style={style}>
      <LexicalComposer initialConfig={initialConfig}>
        {!readOnly && (
          <ToolbarPlugin
            onAttach={onAttach}
            templates={templates}
            onSelectTemplate={onSelectTemplate}
            categories={categories}
            categoryValue={categoryValue}
            onChangeCategory={onChangeCategory}
            onManageCategories={onManageCategories}
            isFullscreen={isFullscreen}
            onToggleFullscreen={onToggleFullscreen}
          />
        )}
        <div
          className={cn(
            "tw:relative",
            isFullscreen && "tw:flex tw:min-h-0 tw:flex-1 tw:flex-col"
          )}
        >
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                spellCheck
                aria-label={ariaLabel ?? placeholder}
                onKeyDown={stopShortcutPropagation}
                className={cn(
                  readOnly
                    ? "tw:outline-none"
                    : "tw:min-h-20 tw:px-3 tw:py-2 tw:outline-none",
                  contentClassName
                )}
              />
            }
            placeholder={
              placeholder && !readOnly ? (
                <div className="tw:pointer-events-none tw:absolute tw:top-2 tw:left-3 tw:text-sm tw:text-[var(--color-text-muted)] tw:select-none">
                  {placeholder}
                </div>
              ) : null
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
        <HistoryPlugin />
        <ListPlugin />
        <TabIndentationPlugin maxIndent={4} />
        <LinkPlugin validateUrl={isSafeUrl} />
        {!readOnly && <MentionsPlugin />}
        {!readOnly && <LinkHoverPlugin />}
        <OnChangePlugin ignoreSelectionChange onChange={handleChange} />
        <EditablePlugin readOnly={readOnly} />
        <EditorApiPlugin
          apiRef={ref}
          editorState={editorState}
          content={content}
        />
        {autoFocus && <AutoFocusPlugin defaultSelection="rootEnd" />}
      </LexicalComposer>
    </div>
  );
});

RichTextEditor.propTypes = {
  editorState: PropTypes.object,
  content: PropTypes.string,
  onStateChange: PropTypes.func,
  readOnly: PropTypes.bool,
  style: PropTypes.object,
  className: PropTypes.string,
  contentClassName: PropTypes.string,
  placeholder: PropTypes.string,
  // Names the editable region when the visible label is not the placeholder,
  // as it is inside a Field with its own FieldLabel.
  ariaLabel: PropTypes.string,
  autoFocus: PropTypes.bool,
  onAttach: PropTypes.func,
  // Note-oriented toolbar controls, passed as plain data and callbacks so no
  // note-domain component enters the editor. A control is hidden when its
  // handler is absent, so only note composers show them.
  templates: PropTypes.arrayOf(PropTypes.object),
  onSelectTemplate: PropTypes.func,
  categories: PropTypes.arrayOf(PropTypes.object),
  categoryValue: PropTypes.number,
  onChangeCategory: PropTypes.func,
  onManageCategories: PropTypes.func,
  isFullscreen: PropTypes.bool,
  onToggleFullscreen: PropTypes.func,
};

export default RichTextEditor;
